import React, { useState, useEffect } from 'react';
import { getReportGlobal, getCommerciaux, getZones, getEquipes } from '../services/api';
import Chart from 'react-apexcharts';
import { FiFileText, FiDownload, FiUsers, FiMapPin, FiBox, FiBarChart2, FiFilter, FiTrendingUp, FiTrendingDown, FiTarget, FiActivity, FiDollarSign, FiShoppingCart, FiPercent } from 'react-icons/fi';

const MOIS = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];
const JOURS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

type ReportTab = 'global' | 'commerciaux' | 'zones' | 'equipes' | 'produits';

const Reporting: React.FC = () => {
    const [report, setReport] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [dateDebut, setDateDebut] = useState(new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]);
    const [dateFin, setDateFin] = useState(new Date().toISOString().split('T')[0]);
    const [tab, setTab] = useState<ReportTab>('global');
    const [exporting, setExporting] = useState(false);
    const [equipes, setEquipes] = useState<any[]>([]);
    const [filterEquipe, setFilterEquipe] = useState('');

    useEffect(() => {
        getEquipes().then(res => setEquipes(res.data)).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        getReportGlobal({ date_debut: dateDebut, date_fin: dateFin, equipe_id: filterEquipe || undefined })
            .then(res => setReport(res.data))
            .finally(() => setLoading(false));
    }, [dateDebut, dateFin, filterEquipe]);

    const handleExport = async (format: 'pdf' | 'csv', type?: string) => {
        setExporting(true);
        try {
            const token = localStorage.getItem('sp_token');
            const endpoint = format === 'pdf' ? 'export/pdf' : 'export/csv';
            const params = new URLSearchParams({ date_debut: dateDebut, date_fin: dateFin });
            if (type) params.set('type', type);
            const resp = await fetch(`http://localhost:5055/api/reporting/${endpoint}?${params}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `rapport_${type || 'global'}_${dateDebut}_${dateFin}.${format === 'pdf' ? 'pdf' : 'csv'}`;
            a.click();
            URL.revokeObjectURL(url);
        } catch { alert('Erreur export'); }
        setExporting(false);
    };

    if (loading) return <div className="loading">Chargement...</div>;
    if (!report) return <div className="error">Erreur de chargement</div>;

    const n = (v: any) => Math.round(Number(v) || 0); // parse MySQL decimal strings to int

    const calcTrend = (cur: number, prev: number): number | null => {
        if (!prev && !cur) return null;
        if (!prev) return 100;
        return Math.round(((cur - prev) / prev) * 100);
    };

    const fmtNum = (v: any) => {
        const num = n(v);
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return Math.round(num / 1000) + 'K';
        return num.toLocaleString();
    };

    const fmtCA = (v: any) => n(v).toLocaleString() + ' F';

    const tabs: { key: ReportTab; label: string; icon: React.ReactNode }[] = [
        { key: 'global', label: 'Vue globale', icon: <FiBarChart2 size={15} /> },
        { key: 'commerciaux', label: 'Par commercial', icon: <FiUsers size={15} /> },
        { key: 'zones', label: 'Par zone', icon: <FiMapPin size={15} /> },
        { key: 'equipes', label: 'Par equipe', icon: <FiUsers size={15} /> },
        { key: 'produits', label: 'Par produit', icon: <FiBox size={15} /> },
    ];

    return (
        <div className="page-reporting">
            <div className="page-header">
                <h2><FiFileText /> Reporting & Analytics</h2>
                <div className="header-actions">
                    <button className="btn btn-outline" onClick={() => handleExport('csv', tab === 'global' ? undefined : tab)} disabled={exporting}>
                        <FiDownload /> {exporting ? 'Export...' : 'Excel/CSV'}
                    </button>
                    <button className="btn btn-outline" onClick={() => handleExport('pdf')} disabled={exporting} style={{ marginLeft: 6 }}>
                        <FiFileText /> PDF
                    </button>
                </div>
            </div>

            <div className="filters-bar" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <FiFilter size={15} />
                <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} />
                <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} />
                <select value={filterEquipe} onChange={e => setFilterEquipe(e.target.value)}
                    style={{ padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13 }}>
                    <option value="">Toutes les equipes</option>
                    {equipes.map(eq => <option key={eq.id} value={eq.id}>{eq.nom}</option>)}
                </select>
                {filterEquipe && <button className="btn btn-outline btn-sm" onClick={() => setFilterEquipe('')} style={{ fontSize: 12 }}>Reinitialiser</button>}
            </div>

            <div className="tabs" style={{ marginBottom: 16 }}>
                {tabs.map(t => (
                    <button key={t.key} className={`tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
                        {t.icon} <span style={{ marginLeft: 5 }}>{t.label}</span>
                    </button>
                ))}
            </div>

            {tab === 'global' && (() => {
                const r = report.resume || {};
                const pr = report.prev_resume || {};
                const tAct = calcTrend(n(r.total_activites), n(pr.total_activites));
                const tCA = calcTrend(n(r.ca_total), n(pr.ca_total));
                const tCom = calcTrend(n(r.commerciaux_actifs), n(pr.commerciaux_actifs));
                const tCli = calcTrend(n(r.clients_touches), n(pr.clients_touches));
                const obj = report.objectifs || {};
                const conv = report.conversion || {};
                const maxZoneCa = Math.max(...(report.par_zone || []).map((z: any) => n(z.ca)), 1);
                const maxComCa = Math.max(...(report.top_commerciaux || []).map((c: any) => n(c.ca)), 1);

                return <>
                    {/* KPI Row */}
                    <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', marginBottom: 18 }}>
                        {[
                            { label: 'Activites', value: r.total_activites || 0, trend: tAct, color: '#16A34A', fmt: false },
                            { label: 'CA Total', value: r.ca_total || 0, trend: tCA, color: '#3B82F6', fmt: true },
                            { label: 'Commerciaux actifs', value: r.commerciaux_actifs || 0, trend: tCom, color: '#8B5CF6', fmt: false },
                            { label: 'Clients touches', value: r.clients_touches || 0, trend: tCli, color: '#F59E0B', fmt: false },
                            { label: 'Taux conversion', value: conv.taux || 0, trend: null, color: '#EF4444', fmt: false, suffix: '%' },
                        ].map((k, i) => (
                            <div key={i} className="kpi-card" style={{ borderLeftColor: k.color }}>
                                <div className="kpi-content">
                                    <span className="kpi-value">
                                        {k.fmt ? fmtNum(k.value) : k.value.toLocaleString()}{k.suffix || (k.fmt ? ' F' : '')}
                                    </span>
                                    <span className="kpi-label">{k.label}</span>
                                    {k.trend !== null && (
                                        <span style={{ fontSize: 11, marginTop: 4, color: k.trend >= 0 ? '#16A34A' : '#EF4444', display: 'flex', alignItems: 'center', gap: 3 }}>
                                            {k.trend >= 0 ? <FiTrendingUp size={12} /> : <FiTrendingDown size={12} />}
                                            {k.trend >= 0 ? '+' : ''}{k.trend}% vs prec.
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Row 2: Evolution + Conversion funnel + Objectifs */}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                        <div className="card" style={{ padding: 18 }}>
                            <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 600 }}>Evolution mensuelle</h3>
                            <Chart options={{
                                chart: { type: 'area', toolbar: { show: true }, fontFamily: 'DM Sans, sans-serif', zoom: { enabled: true } },
                                colors: ['#16A34A', '#3B82F6'],
                                xaxis: { categories: (report.par_mois || []).map((m: any) => MOIS[m.mois - 1] + ' ' + m.annee) },
                                yaxis: [
                                    { title: { text: 'Activites' }, labels: { formatter: (v: number) => Math.round(v).toString() } },
                                    { opposite: true, title: { text: 'CA (F)' }, labels: { formatter: (v: number) => fmtNum(v) } }
                                ],
                                stroke: { curve: 'smooth', width: [2, 3] },
                                fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05 } },
                                legend: { position: 'top' },
                                tooltip: { shared: true, intersect: false, y: { formatter: (v: number, opts: any) => opts.seriesIndex === 1 ? v.toLocaleString() + ' F' : v.toString() } },
                                grid: { borderColor: '#E5E7EB', strokeDashArray: 4 },
                                dataLabels: { enabled: false },
                            }} series={[
                                { name: 'Activites', type: 'area', data: (report.par_mois || []).map((m: any) => m.nb || 0) },
                                { name: 'CA (F)', type: 'line', data: (report.par_mois || []).map((m: any) => n(m.ca)) }
                            ]} type="area" height={280} />
                        </div>

                        <div className="card" style={{ padding: 18 }}>
                            <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 600 }}>Entonnoir de conversion</h3>
                            {[
                                { label: 'Visites', val: (report.par_type || []).find((t: any) => t.type === 'visite')?.nb || 0, color: '#3B82F6' },
                                { label: 'Ventes', val: (report.par_type || []).find((t: any) => t.type === 'vente')?.nb || 0, color: '#16A34A' },
                                { label: 'Commandes', val: (report.par_type || []).find((t: any) => t.type === 'commande')?.nb || 0, color: '#F59E0B' },
                                { label: 'Installations', val: (report.par_type || []).find((t: any) => t.type === 'installation')?.nb || 0, color: '#EF4444' },
                            ].map((f, i, arr) => {
                                const maxVal = Math.max(...arr.map(x => x.val), 1);
                                const taux = i > 0 && arr[i - 1].val > 0 ? Math.round((f.val / arr[i - 1].val) * 100) : null;
                                return (
                                    <div key={i} style={{ marginBottom: 14 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                            <span style={{ fontSize: 13, fontWeight: 500 }}>{f.label}</span>
                                            <span style={{ fontSize: 14, fontWeight: 700, color: f.color }}>{f.val}</span>
                                        </div>
                                        <div style={{ height: 10, background: '#f3f4f6', borderRadius: 5, overflow: 'hidden' }}>
                                            <div style={{ height: '100%', width: `${(f.val / maxVal) * 100}%`, background: f.color, borderRadius: 5, transition: 'width 0.5s' }} />
                                        </div>
                                        {taux !== null && <span style={{ fontSize: 11, color: '#999' }}>Taux: {taux}%</span>}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="card" style={{ padding: 18 }}>
                            <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 600 }}><FiTarget style={{ marginRight: 6 }} />Objectifs</h3>
                            <div style={{ textAlign: 'center', marginBottom: 14 }}>
                                <Chart options={{
                                    chart: { type: 'radialBar', fontFamily: 'DM Sans, sans-serif' },
                                    plotOptions: { radialBar: { hollow: { size: '60%' }, dataLabels: { name: { show: true, fontSize: '13px' }, value: { show: true, fontSize: '22px', fontWeight: 700, formatter: (v: number) => v + '%' } } } },
                                    labels: ['Realisation'],
                                    colors: [(obj.taux_moyen || 0) >= 80 ? '#16A34A' : (obj.taux_moyen || 0) >= 50 ? '#F59E0B' : '#EF4444'],
                                }} series={[obj.taux_moyen || 0]} type="radialBar" height={180} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
                                <div>
                                    <div style={{ fontSize: 20, fontWeight: 700, color: '#16A34A' }}>{obj.atteints || 0}</div>
                                    <div style={{ fontSize: 11, color: '#666' }}>Atteints</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: 20, fontWeight: 700, color: '#999' }}>{obj.total || 0}</div>
                                    <div style={{ fontSize: 11, color: '#666' }}>Total</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Row 3: Top Commerciaux + Par Zone */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                        <div className="card" style={{ padding: 18 }}>
                            <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600 }}><FiUsers style={{ marginRight: 6 }} />Top 10 Commerciaux</h3>
                            <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                                {(report.top_commerciaux || []).map((c: any, i: number) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                                        <div style={{ width: 26, height: 26, borderRadius: '50%', background: i < 3 ? ['#FFD700', '#C0C0C0', '#CD7F32'][i] : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: i < 3 ? '#fff' : '#666', flexShrink: 0 }}>{i + 1}</div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.nom}</div>
                                            <div style={{ fontSize: 11, color: '#888' }}>{c.zone || 'N/A'} &bull; {c.nb_activites} act. &bull; {c.clients} cli.</div>
                                        </div>
                                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                            <div style={{ fontWeight: 700, fontSize: 14, color: '#16A34A' }}>{fmtCA(c.ca)}</div>
                                            <div style={{ height: 4, width: 80, background: '#f3f4f6', borderRadius: 2, marginTop: 3 }}>
                                                <div style={{ height: '100%', width: `${(n(c.ca) / maxComCa) * 100}%`, background: '#16A34A', borderRadius: 2 }} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {!(report.top_commerciaux || []).length && <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>Aucune donnee</div>}
                            </div>
                        </div>

                        <div className="card" style={{ padding: 18 }}>
                            <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600 }}><FiMapPin style={{ marginRight: 6 }} />Performance par zone</h3>
                            <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                                {(report.par_zone || []).filter((z: any) => z.nb > 0).map((z: any, i: number) => (
                                    <div key={i} style={{ padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                            <span style={{ fontWeight: 600, fontSize: 13 }}>{z.zone || 'Non assigne'}</span>
                                            <span style={{ fontWeight: 700, fontSize: 14, color: '#8B5CF6' }}>{fmtCA(z.ca)}</span>
                                        </div>
                                        <div style={{ height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden', marginBottom: 4 }}>
                                            <div style={{ height: '100%', width: `${(n(z.ca) / maxZoneCa) * 100}%`, background: 'linear-gradient(90deg, #8B5CF6, #A78BFA)', borderRadius: 4, transition: 'width 0.5s' }} />
                                        </div>
                                        <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#888' }}>
                                            <span>{z.nb} activites</span>
                                            <span>{z.commerciaux || 0} commerciaux</span>
                                            <span>{z.clients || 0} clients</span>
                                        </div>
                                    </div>
                                ))}
                                {!(report.par_zone || []).filter((z: any) => z.nb > 0).length && <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>Aucune donnee</div>}
                            </div>
                        </div>
                    </div>

                    {/* Row 4: Repartition type donut + Top produits + Par jour */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                        <div className="card" style={{ padding: 18 }}>
                            <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 600 }}>Repartition par type</h3>
                            <Chart options={{
                                chart: { type: 'donut', fontFamily: 'DM Sans, sans-serif' },
                                labels: (report.par_type || []).map((t: any) => t.type),
                                colors: ['#16A34A', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#F97316', '#14B8A6'],
                                legend: { position: 'bottom', fontSize: '12px' },
                                plotOptions: { pie: { donut: { size: '58%', labels: { show: true, total: { show: true, label: 'Total', fontSize: '13px' } } } } },
                            }} series={(report.par_type || []).map((t: any) => t.nb)} type="donut" height={260} />
                        </div>

                        <div className="card" style={{ padding: 18 }}>
                            <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600 }}><FiBox style={{ marginRight: 6 }} />Top produits</h3>
                            {(report.top_produits || []).slice(0, 7).map((p: any, i: number) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f3f4f6' }}>
                                    <span style={{ width: 22, height: 22, borderRadius: '50%', background: i < 3 ? '#16A34A' : '#e5e7eb', color: i < 3 ? '#fff' : '#666', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.produit}</div>
                                        <div style={{ fontSize: 11, color: '#888' }}>{p.categorie} &bull; {p.nb_ventes} ventes</div>
                                    </div>
                                    <span style={{ fontWeight: 700, fontSize: 13, color: '#16A34A', flexShrink: 0 }}>{fmtCA(p.ca)}</span>
                                </div>
                            ))}
                            {!(report.top_produits || []).length && <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>Aucune donnee</div>}
                        </div>

                        <div className="card" style={{ padding: 18 }}>
                            <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 600 }}>Activite par jour</h3>
                            <Chart options={{
                                chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'DM Sans, sans-serif' },
                                plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
                                colors: ['#8B5CF6'],
                                xaxis: { categories: (report.par_jour || []).map((j: any) => JOURS[j.jour - 1] || j.jour) },
                                yaxis: { labels: { formatter: (v: number) => Math.round(v).toString() } },
                                tooltip: { y: { formatter: (v: number) => v + ' activites' } },
                                grid: { borderColor: '#E5E7EB', strokeDashArray: 4 },
                                dataLabels: { enabled: false },
                            }} series={[{ name: 'Activites', data: (report.par_jour || []).map((j: any) => j.nb) }]} type="bar" height={260} />
                        </div>
                    </div>

                    {/* Row 5: CA par type bar */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 14 }}>
                        <div className="card" style={{ padding: 18 }}>
                            <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 600 }}>CA par type d'activite</h3>
                            <Chart options={{
                                chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'DM Sans, sans-serif' },
                                plotOptions: { bar: { horizontal: true, borderRadius: 6, barHeight: '55%' } },
                                colors: ['#3B82F6'],
                                xaxis: { labels: { formatter: (v: string) => fmtNum(Number(v)) + ' F' } },
                                tooltip: { y: { formatter: (v: number) => v.toLocaleString() + ' F' } },
                                grid: { borderColor: '#E5E7EB', strokeDashArray: 4 },
                                dataLabels: { enabled: true, formatter: (v: number) => v.toLocaleString() + ' F', style: { fontSize: '11px' } },
                            }} series={[{ name: 'CA', data: (report.par_type || []).map((t: any) => ({ x: t.type, y: n(t.ca) })) }]} type="bar" height={Math.max(180, (report.par_type || []).length * 50)} />
                        </div>
                    </div>
                </>;
            })()}

            {tab === 'commerciaux' && <CommerciauView dateDebut={dateDebut} dateFin={dateFin} />}
            {tab === 'zones' && <ZonesView dateDebut={dateDebut} dateFin={dateFin} />}

            {tab === 'equipes' && (() => {
                const eqs = report.par_equipe || [];
                const maxCa = Math.max(...eqs.map((e: any) => n(e.ca)), 1);
                return (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
                        {eqs.map((eq: any) => (
                            <div key={eq.id} style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
                                <div style={{ height: 4, background: eq.couleur || '#3B82F6' }} />
                                <div style={{ padding: '18px 20px' }}>
                                    <h3 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 700, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: eq.couleur || '#3B82F6' }} />
                                        {eq.equipe}
                                    </h3>
                                    <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 14 }}>
                                        <div className="kpi-card" style={{ borderLeftColor: eq.couleur || '#3B82F6' }}>
                                            <div className="kpi-content">
                                                <span className="kpi-value">{n(eq.commerciaux)}</span>
                                                <span className="kpi-label">Commerciaux</span>
                                            </div>
                                        </div>
                                        <div className="kpi-card" style={{ borderLeftColor: '#16A34A' }}>
                                            <div className="kpi-content">
                                                <span className="kpi-value">{n(eq.nb_activites)}</span>
                                                <span className="kpi-label">Activites</span>
                                            </div>
                                        </div>
                                        <div className="kpi-card" style={{ borderLeftColor: '#3B82F6' }}>
                                            <div className="kpi-content">
                                                <span className="kpi-value">{fmtCA(eq.ca)}</span>
                                                <span className="kpi-label">CA</span>
                                            </div>
                                        </div>
                                        <div className="kpi-card" style={{ borderLeftColor: '#8B5CF6' }}>
                                            <div className="kpi-content">
                                                <span className="kpi-value">{n(eq.clients)}</span>
                                                <span className="kpi-label">Clients</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ height: 7, background: '#F1F5F9', borderRadius: 4, overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${(n(eq.ca) / maxCa) * 100}%`, background: `linear-gradient(90deg, ${eq.couleur || '#3B82F6'}, ${eq.couleur || '#3B82F6'}AA)`, borderRadius: 4, transition: 'width 0.6s ease' }} />
                                    </div>
                                    {n(eq.commerciaux) > 0 && (
                                        <div style={{ fontSize: 12, color: '#64748B', marginTop: 10, textAlign: 'center' }}>
                                            CA moyen/commercial: <strong style={{ color: '#1E293B' }}>{fmtCA(n(eq.ca) / n(eq.commerciaux))}</strong>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        {eqs.length === 0 && (
                            <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', textAlign: 'center', padding: 40, color: '#94A3B8' }}>Aucune equipe trouvee</div>
                        )}
                    </div>
                );
            })()}

            {tab === 'produits' && <ProduitsView dateDebut={dateDebut} dateFin={dateFin} />}
        </div>
    );
};

// Sub-view: Par Commercial
const CommerciauView: React.FC<{ dateDebut: string; dateFin: string }> = ({ dateDebut, dateFin }) => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const token = localStorage.getItem('sp_token');
        fetch(`http://localhost:5055/api/reporting/export/csv?date_debut=${dateDebut}&date_fin=${dateFin}&type=commerciaux&_json=1`, {
            headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {});
        // Use global report data enriched
        getReportGlobal({ date_debut: dateDebut, date_fin: dateFin })
            .then(res => {
                // We need commercial-level data — fetch via dedicated endpoint
                return fetch(`http://localhost:5055/api/performance/classement?periode=custom&date_debut=${dateDebut}&date_fin=${dateFin}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            })
            .then(r => r.json())
            .then(d => setData(Array.isArray(d) ? d : d.data || []))
            .catch(() => setData([]))
            .finally(() => setLoading(false));
    }, [dateDebut, dateFin]);

    if (loading) return <div className="loading">Chargement...</div>;

    return (
        <div className="table-container">
            <table className="data-table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Commercial</th>
                        <th>Activites</th>
                        <th>CA (F)</th>
                        <th>Clients</th>
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 ? (
                        <tr><td colSpan={5} style={{ textAlign: 'center', padding: 30, color: '#999' }}>Aucune donnee pour cette periode</td></tr>
                    ) : data.map((c: any, i: number) => (
                        <tr key={c.id || i}>
                            <td>{i + 1}</td>
                            <td><strong>{c.commercial || c.nom}</strong></td>
                            <td>{c.nb_activites || c.activites || 0}</td>
                            <td style={{ color: '#16A34A', fontWeight: 600 }}>{(c.ca || 0).toLocaleString()} F</td>
                            <td>{c.clients || 0}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// Sub-view: Par Zone
const ZonesView: React.FC<{ dateDebut: string; dateFin: string }> = ({ dateDebut, dateFin }) => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getReportGlobal({ date_debut: dateDebut, date_fin: dateFin })
            .then(res => setData(res.data?.par_zone || []))
            .finally(() => setLoading(false));
    }, [dateDebut, dateFin]);

    if (loading) return <div className="loading">Chargement...</div>;

    const maxCa = Math.max(...data.map(z => Math.round(Number(z.ca) || 0)), 1);

    return (
        <div>
            <div className="cards-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
                {data.length === 0 ? (
                    <p style={{ color: '#999', padding: 30 }}>Aucune donnee</p>
                ) : data.map((z: any, i: number) => (
                    <div key={i} className="kpi-card" style={{ borderLeftColor: '#8B5CF6' }}>
                        <div className="kpi-content">
                            <span className="kpi-label" style={{ fontSize: 14, fontWeight: 600, color: '#333' }}><FiMapPin /> {z.zone || 'Non assigne'}</span>
                            <div style={{ marginTop: 10 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span style={{ fontSize: 12, color: '#666' }}>{z.nb || 0} activites</span>
                                    <span style={{ fontSize: 14, fontWeight: 700, color: '#16A34A' }}>{Math.round(Number(z.ca) || 0).toLocaleString()} F</span>
                                </div>
                                <div style={{ height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${(Math.round(Number(z.ca) || 0) / maxCa) * 100}%`, background: '#8B5CF6', borderRadius: 3 }} />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Sub-view: Par Produit
const ProduitsView: React.FC<{ dateDebut: string; dateFin: string }> = ({ dateDebut, dateFin }) => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const token = localStorage.getItem('sp_token');
        fetch(`http://localhost:5055/api/reporting/export/csv?date_debut=${dateDebut}&date_fin=${dateFin}&type=produits&_json=1`, {
            headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {});
        // Get product stats from global report — or build from activites
        fetch(`http://localhost:5055/api/reporting/global?date_debut=${dateDebut}&date_fin=${dateFin}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(r => r.json())
            .then(d => {
                // par_type contains activity types, not products — show par_type for now
                setData(d.par_type || []);
            })
            .catch(() => setData([]))
            .finally(() => setLoading(false));
    }, [dateDebut, dateFin]);

    if (loading) return <div className="loading">Chargement...</div>;

    return (
        <div className="table-container">
            <table className="data-table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Type / Produit</th>
                        <th>Nb Activites</th>
                        <th>CA (F)</th>
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 ? (
                        <tr><td colSpan={4} style={{ textAlign: 'center', padding: 30, color: '#999' }}>Aucune donnee</td></tr>
                    ) : data.map((p: any, i: number) => (
                        <tr key={i}>
                            <td>{i + 1}</td>
                            <td><strong>{p.type || p.produit || '-'}</strong></td>
                            <td>{p.nb || p.nb_activites || 0}</td>
                            <td style={{ color: '#16A34A', fontWeight: 600 }}>{(p.ca || 0).toLocaleString()} F</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default Reporting;
